import React, { useState, useEffect } from "react";
import useApi from "./useApi";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
    IconButton,
    Modal,
    Box,
    Typography,
    Button,
    TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const Projects = () => {
    const [newProjectName, setNewProjectName] = useState("");
    const [newProjectDescription, setNewProjectDescription] = useState("");
    const [currentProject, setCurrentProject] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [editProjectName, setEditProjectName] = useState("");
    const [editProjectDescription, setEditProjectDescription] = useState("");
    const [{ data: projects, loading }, callApi] = useApi();

    useEffect(() => {
        callApi({ url: "/api/projects" });
    }, [callApi]);
    const openDeleteModalHandler = (project) => {
        setCurrentProject(project);
        setOpenDeleteModal(true);
    };

    const handleDeleteProject = () => {
        if (currentProject) {
            callApi({
                url: `/api/projects/${currentProject.id}`,
                method: "DELETE",
            })
                .then(() => {
                    toast.success("Project deleted successfully!");
                    setOpenDeleteModal(false);
                    callApi({ url: `/api/projects` });
                })
                .catch((error) => {
                    toast.error("Failed to delete project.");
                    console.error("Delete project error:", error);
                });
        }
    };

    const openEditModalHandler = (project) => {
        setCurrentProject(project);
        setEditProjectName(project.name);
        setEditProjectDescription(
            project.description ? project.description : "",
        );
        setOpenEditModal(true);
    };

    const handleEditProject = (e) => {
        e.preventDefault();
        if (currentProject) {
            callApi({
                url: `/api/projects/${currentProject.id}`,
                method: "PUT",
                body: {
                    name: editProjectName,
                    description: editProjectDescription,
                },
            })
                .then(() => {
                    toast.success("Project updated successfully!");
                    setOpenEditModal(false);
                    callApi({ url: `/api/projects` }); // Re-fetch projects
                })
                .catch((error) => {
                    toast.error("Failed to update project.");
                    console.error("Update project error:", error);
                });
        }
    };

    const handleCreateProject = async (e) => {
        e.preventDefault();
        callApi({
            url: "/api/projects",
            method: "POST",
            body: { name: newProjectName, description: newProjectDescription },
        }).then(() => {
            toast.success("Project created successfully!");
            setNewProjectName("");
            setNewProjectDescription("");
            callApi({ url: "/api/projects" }); // Re-fetch projects to update the list
        });
    };

    return (
        <div className="content-container">
            <h2>Projects</h2>
            {loading && <div>Loading...</div>}
            <form onSubmit={handleCreateProject} className="form-container">
                <input
                    type="text"
                    placeholder="Project Name"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    required
                    className="form-input"
                />
                <textarea
                    placeholder="Project Description"
                    value={newProjectDescription}
                    onChange={(e) => setNewProjectDescription(e.target.value)}
                    required
                    className="form-textarea"
                />
                <button type="submit" className="form-button">
                    Create New Project
                </button>
            </form>
            <ul className="item-list">
                {Array.isArray(projects) ? (
                    projects.map((project) => (
                        <li key={project.id} className="list-item">
                            <Link
                                to={`/projects/${project.id}/packages`}
                                className="item-link"
                            >
                                {project.name}
                            </Link>
                            <IconButton
                                onClick={() => openEditModalHandler(project)}
                                size="small"
                            >
                                <EditIcon />
                            </IconButton>
                            <IconButton
                                onClick={() => openDeleteModalHandler(project)}
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </li>
                    ))
                ) : (
                    <p>No projects found.</p>
                )}
            </ul>
            <Modal
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
                aria-labelledby="delete-modal-title"
                aria-describedby="delete-modal-description"
            >
                <Box className="modal">
                    <Typography id="delete-modal-title" variant="h6">
                        Confirm Project Deletion
                    </Typography>
                    <Typography id="delete-modal-description" sx={{ mt: 2 }}>
                        Are you sure you want to delete this project?
                    </Typography>
                    <Button onClick={handleDeleteProject} color="error">
                        Delete
                    </Button>
                    <Button onClick={() => setOpenDeleteModal(false)}>
                        Cancel
                    </Button>
                </Box>
            </Modal>
            <Modal
                open={openEditModal}
                onClose={() => setOpenEditModal(false)}
                aria-labelledby="edit-modal-title"
                aria-describedby="edit-modal-description"
            >
                <Box
                    className="modal"
                    component="form"
                    onSubmit={handleEditProject}
                >
                    <Typography id="edit-modal-title" variant="h6">
                        Edit Project
                    </Typography>
                    <TextField
                        margin="dense"
                        id="name"
                        label="Project Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={editProjectName}
                        onChange={(e) => setEditProjectName(e.target.value)}
                        required
                    />
                    <TextField
                        margin="dense"
                        id="description"
                        label="Project Description"
                        type="text"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        value={editProjectDescription}
                        onChange={(e) =>
                            setEditProjectDescription(e.target.value)
                        }
                    />
                    <Button type="submit" color="primary">
                        Update
                    </Button>
                    <Button onClick={() => setOpenEditModal(false)}>
                        Cancel
                    </Button>
                </Box>
            </Modal>
        </div>
    );
};

export default Projects;
